import React, { useState, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { useCourse, useUpdateCourse } from '@/hooks/useCourses'
import { useModulesByCourse, usePublishModule, useCreateModule, useUpdateModule, useDeleteModule } from '@/hooks/useModules'
import { InstructorAssignmentPanel } from '@/components/instructors'
import { ModuleStatusBadge, PublishModuleModal, ModuleFormModal } from '@/components/modules'
import { ImageWithFallback } from '@/components/common/ImageWithFallback'
import { AffiliateCourseCommissionPanel } from '@/components/affiliates'
import { uploadService } from '@/services/uploadService'
import { affiliateService } from '@/services/affiliateService'
import type { Module, CreateModuleDTO, UpdateModuleDTO } from '@/types/module'
import type { AffiliateCourseCommission } from '@/types/affiliate'

export const AdminCourseBuilderPage: React.FC = () => {
    const { id: courseId } = useParams<{ id: string }>()
    const { data: course, isLoading: isLoadingCourse } = useCourse(courseId || '')
    const { data: modules, isLoading: isLoadingModules } = useModulesByCourse(courseId || '')
    const updateCourse = useUpdateCourse()

    const [publishingModule, setPublishingModule] = useState<Module | null>(null)
    const publishModule = usePublishModule()

    // Thumbnail upload state
    const [isUploadingThumbnail, setIsUploadingThumbnail] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Module CRUD state
    const [showModuleModal, setShowModuleModal] = useState(false)
    const [editingModule, setEditingModule] = useState<Module | null>(null)
    const [deletingModuleId, setDeletingModuleId] = useState<string | null>(null)
    const [moduleError, setModuleError] = useState<string | null>(null)

    // Module mutations
    const createModule = useCreateModule()
    const updateModule = useUpdateModule()
    const deleteModule = useDeleteModule()

    // Affiliate commissions state
    const [affiliateCommissions, setAffiliateCommissions] = useState<AffiliateCourseCommission[]>([])

    // Fetch affiliate commissions for this course
    const fetchCommissions = async () => {
        if (!courseId) return
        try {
            const data = await affiliateService.getCourseCommissions(courseId)
            setAffiliateCommissions(data)
        } catch (error) {
            console.error('Failed to fetch commissions:', error)
            setAffiliateCommissions([])
        }
    }

    React.useEffect(() => {
        fetchCommissions()
    }, [courseId])

    const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file || !courseId) return

        setIsUploadingThumbnail(true)
        try {
            const res = await uploadService.uploadImage(file)
            if (!res.success) throw new Error(res.error?.message || 'Failed to upload image')

            await updateCourse.mutateAsync({
                id: courseId,
                data: { thumbnailUrl: res.data!.url }
            })
        } catch (error) {
            alert(error instanceof Error ? error.message : 'An error occurred during upload')
        } finally {
            setIsUploadingThumbnail(false)
        }
    }

    const handlePublishModule = async () => {
        if (!publishingModule) return
        await publishModule.mutateAsync(publishingModule.id)
        setPublishingModule(null)
    }

    // Module handlers
    const handleAddModule = () => {
        setEditingModule(null)
        setModuleError(null)
        setShowModuleModal(true)
    }

    const handleEditModule = (module: Module) => {
        setEditingModule(module)
        setModuleError(null)
        setShowModuleModal(true)
    }

    const handleDeleteModule = async (moduleId: string) => {
        if (!confirm('Are you sure you want to delete this module? All lessons in this module will also be deleted.')) return

        setDeletingModuleId(moduleId)
        try {
            await deleteModule.mutateAsync(moduleId)
        } catch (error) {
            console.error('Failed to delete module:', error)
        } finally {
            setDeletingModuleId(null)
        }
    }

    const handleSaveModule = async (data: CreateModuleDTO | UpdateModuleDTO) => {
        setModuleError(null)
        try {
            if (editingModule) {
                await updateModule.mutateAsync({ id: editingModule.id, data: data as UpdateModuleDTO })
            } else {
                await createModule.mutateAsync(data as CreateModuleDTO)
            }
            setShowModuleModal(false)
            setEditingModule(null)
        } catch (error) {
            console.error('Failed to save module:', error)
            const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
            setModuleError(errorMessage)
        }
    }

    if (isLoadingCourse) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        )
    }

    if (!course) {
        return (
            <div className="text-center py-20">
                <span className="material-symbols-outlined text-4xl text-red-400 mb-4">error</span>
                <p className="text-red-400">Course not found</p>
            </div>
        )
    }

    return (
        <div>
            <PageHeader
                title={course.title}
                subtitle="Manage modules and instructors"
                breadcrumbs={[
                    { label: 'Dashboard', to: '/admin/dashboard' },
                    { label: 'Courses', to: '/admin/courses' },
                    { label: course.title }
                ]}
                actions={
                    <Link
                        to="/admin/courses"
                        className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white rounded-lg font-bold text-sm hover:bg-white/20 transition-colors"
                    >
                        <span className="material-symbols-outlined text-[18px]">arrow_back</span>
                        Back to Courses
                    </Link>
                }
            />

            {/* Course Info Card */}
            <div className="group relative rounded-[24px] bg-white dark:bg-surface-card border border-slate-200 dark:border-white/5 p-8 mb-8 overflow-hidden shadow-sm hover:shadow-xl hover:shadow-primary/5 transition-all duration-500">
                <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 transition-transform duration-700">
                    <span className="material-symbols-outlined text-[120px] text-primary">school</span>
                </div>
                
                <div className="relative flex flex-col lg:flex-row lg:items-center gap-8">
                    <div 
                        className={`relative w-40 h-24 rounded-2xl overflow-hidden bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 shrink-0 cursor-pointer group/img ${isUploadingThumbnail ? 'opacity-50' : 'hover:border-primary/50'}`}
                        onClick={() => !isUploadingThumbnail && fileInputRef.current?.click()}
                    >
                        <ImageWithFallback
                            src={course.thumbnailUrl}
                            alt={course.title}
                            className="w-full h-full object-cover group-hover/img:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover/img:opacity-100 transition-opacity flex flex-col items-center justify-center text-white">
                            <span className="material-symbols-outlined text-[24px]">photo_camera</span>
                            <span className="text-xs font-black tracking-wider mt-1">Ganti Sampul</span>
                        </div>
                        {isUploadingThumbnail && (
                            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center">
                                <div className="animate-spin size-6 border-2 border-primary border-t-transparent rounded-full" />
                            </div>
                        )}
                        <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleThumbnailUpload} />
                    </div>

                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                            <span className={`px-3 py-1 rounded-lg text-xs font-black tracking-widest ${
                                course.courseType === 'hybrid' ? 'bg-indigo-500/10 text-indigo-500 border border-indigo-500/20' : 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                            }`}>
                                {course.courseType === 'hybrid' ? 'Hybrid Training' : 'Pure Online'}
                            </span>
                            {course.price > 0 && (
                                <span className="text-sm font-black text-slate-400">
                                    Rp {course.price.toLocaleString('id-ID')}
                                </span>
                            )}
                        </div>
                        <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2 tracking-tight leading-none">{course.title}</h2>
                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed max-w-2xl">
                            {course.shortDescription || course.description}
                        </p>
                    </div>

                    {/* Edit Course Info Button */}
                    <div className="shrink-0">
                        <Link
                            to={`/admin/courses/${courseId}/edit`}
                            className="flex items-center gap-2 px-5 py-3 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 text-sm font-bold hover:bg-primary hover:text-white transition-all border border-slate-200 dark:border-white/10"
                        >
                            <span className="material-symbols-outlined text-[18px]">edit</span>
                            Edit Info Kursus
                        </Link>
                    </div>
                </div>
            </div>

            {/* Modules Section */}
            <div className="space-y-6 mb-10">
                <div className="flex items-center justify-between px-1">
                    <div className="flex items-center gap-3">
                        <div className="size-2 w-2 h-8 bg-primary rounded-full" />
                        <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Kurikulum Pelatihan</h3>
                    </div>
                    <button
                        onClick={handleAddModule}
                        className="flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-black tracking-wider hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-slate-200 dark:shadow-none"
                    >
                        <span className="material-symbols-outlined text-[18px]">add_box</span>
                        Tambah Modul
                    </button>
                </div>

                {isLoadingModules ? (
                    <div className="flex items-center justify-center py-20 bg-white/50 dark:bg-white/[0.02] rounded-[32px] border border-dashed border-slate-200 dark:border-white/10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                ) : modules && modules.length > 0 ? (
                    <div className="grid grid-cols-1 gap-4">
                        {modules.map((module, index) => (
                            <div
                                key={module.id}
                                className="group rounded-[24px] bg-white dark:bg-surface-card border border-slate-200 dark:border-white/5 p-6 hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/5 transition-all duration-500"
                            >
                                <div className="flex items-center gap-6">
                                    {/* Order Number */}
                                    <div className="relative shrink-0">
                                        <div className="size-14 rounded-2xl bg-slate-50 dark:bg-white/[0.03] flex items-center justify-center text-slate-400 group-hover:text-primary transition-colors duration-500">
                                            <span className="material-symbols-outlined text-[28px]">view_agenda</span>
                                        </div>
                                        <div className="absolute -top-2 -left-2 size-6 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center text-xs font-black shadow-lg">
                                            {index + 1}
                                        </div>
                                    </div>

                                    {/* Module Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-1.5">
                                            <h4 className="text-base font-black text-slate-900 dark:text-white truncate tracking-tight">{module.title}</h4>
                                            <ModuleStatusBadge isDraft={module.isDraft} />
                                        </div>
                                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-3 line-clamp-1">
                                            {module.description || 'Modul ini belum memiliki deskripsi detail.'}
                                        </p>
                                        <div className="flex items-center gap-6 text-xs font-black tracking-wider text-slate-400">
                                            <span className="flex items-center gap-1.5">
                                                <span className="material-symbols-outlined text-[16px]">play_circle</span>
                                                {module.videoCount} Materi
                                            </span>
                                            <span className="flex items-center gap-1.5">
                                                <span className="material-symbols-outlined text-[16px]">quiz</span>
                                                {module.quizCount} Quiz
                                            </span>
                                            <span className="flex items-center gap-1.5">
                                                <span className="material-symbols-outlined text-[16px]">schedule</span>
                                                {module.durationHours} Jam
                                            </span>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-3 shrink-0">
                                        {/* Edit Lessons - Primary Action */}
                                        <Link
                                            to={`/admin/modules/${module.id}/lessons`}
                                            className="flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-primary text-white text-sm font-black tracking-tight hover:scale-[1.05] active:scale-[0.95] transition-all shadow-lg shadow-primary/20"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">edit_note</span>
                                            Edit Materi
                                        </Link>

                                        <div className="flex items-center gap-2 border-l border-slate-100 dark:border-white/10 pl-3">
                                            {/* Publish/Unpublish */}
                                            {module.isDraft ? (
                                                <button
                                                    onClick={() => setPublishingModule(module)}
                                                    className="size-11 rounded-xl bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500 hover:text-white transition-all duration-300 flex items-center justify-center"
                                                    title="Publish module"
                                                >
                                                    <span className="material-symbols-outlined text-[20px]">publish</span>
                                                </button>
                                            ) : (
                                                <div className="size-11 rounded-xl bg-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-500/20">
                                                    <span className="material-symbols-outlined text-[20px]">verified</span>
                                                </div>
                                            )}

                                            {/* Edit Module */}
                                            <button
                                                onClick={() => handleEditModule(module)}
                                                className="size-11 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-white/10 transition-all duration-300 flex items-center justify-center"
                                                title="Edit info modul"
                                            >
                                                <span className="material-symbols-outlined text-[20px]">settings</span>
                                            </button>

                                            {/* Delete Module */}
                                            <button
                                                onClick={() => handleDeleteModule(module.id)}
                                                disabled={deletingModuleId === module.id}
                                                className="size-11 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-500 hover:text-rose-500 hover:bg-rose-500/10 transition-all duration-300 flex items-center justify-center disabled:opacity-50"
                                                title="Hapus modul"
                                            >
                                                <span className="material-symbols-outlined text-[20px]">
                                                    {deletingModuleId === module.id ? 'hourglass_empty' : 'delete_sweep'}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-20 bg-white dark:bg-surface-card border-2 border-dashed border-slate-200 dark:border-white/10 rounded-[32px] p-10">
                        <div className="size-24 rounded-full bg-slate-50 dark:bg-white/5 flex items-center justify-center mx-auto mb-6">
                            <span className="material-symbols-outlined text-[48px] text-slate-300">library_add</span>
                        </div>
                        <h4 className="text-xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Kurikulum Kosong</h4>
                        <p className="text-slate-500 dark:text-slate-400 font-medium mb-8 max-w-sm mx-auto">Mulai susun kurikulum pelatihan Anda dengan menambahkan modul pertama.</p>
                        <button
                            onClick={handleAddModule}
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-primary text-white text-sm font-black tracking-wider hover:scale-[1.05] active:scale-[0.95] transition-all shadow-xl shadow-primary/25"
                        >
                            <span className="material-symbols-outlined text-[18px]">add_box</span>
                            Tambah Modul Pertama
                        </button>
                    </div>
                )}
            </div>

            {/* Instructors Section */}
            <div className="rounded-[24px] bg-white dark:bg-surface-card border border-slate-200 dark:border-white/5 p-8 mb-8">
                <div className="flex items-center gap-3 mb-6 px-1">
                    <div className="size-2 w-2 h-8 bg-primary rounded-full" />
                    <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Manajemen Instruktur</h3>
                </div>
                <div className="bg-slate-50 dark:bg-white/[0.01] rounded-[20px] border border-slate-100 dark:border-white/5 p-2">
                    <InstructorAssignmentPanel courseId={courseId || ''} />
                </div>
            </div>

            {/* Affiliate Commissions Section */}
            <div className="rounded-[24px] bg-white dark:bg-surface-card border border-slate-200 dark:border-white/5 p-8">
                <div className="flex items-center gap-3 mb-6 px-1">
                    <div className="size-2 w-2 h-8 bg-amber-500 rounded-full" />
                    <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Komisi Afiliasi</h3>
                </div>
                <div className="bg-slate-50 dark:bg-white/[0.01] rounded-[20px] border border-slate-100 dark:border-white/5 p-2">
                    <AffiliateCourseCommissionPanel
                        mode="by-course"
                        courseId={courseId}
                        commissions={affiliateCommissions}
                        onRefresh={fetchCommissions}
                    />
                </div>
            </div>

            {/* Publish Module Modal */}
            {publishingModule && (
                <PublishModuleModal
                    module={publishingModule}
                    lessons={[]}
                    onPublish={handlePublishModule}
                    onClose={() => setPublishingModule(null)}
                    isPublishing={publishModule.isPending}
                />
            )}

            {/* Module Form Modal */}
            {showModuleModal && courseId && (
                <ModuleFormModal
                    courseId={courseId}
                    module={editingModule}
                    onSave={handleSaveModule}
                    onClose={() => {
                        setShowModuleModal(false)
                        setEditingModule(null)
                        setModuleError(null)
                    }}
                    isSaving={createModule.isPending || updateModule.isPending}
                    error={moduleError}
                />
            )}
        </div>
    )
}

export default AdminCourseBuilderPage
